// apps/web/src/components/chat/ChatClient.tsx
import { useState, useImperativeHandle, forwardRef, useEffect, useRef } from 'react';
import { Box, TextField, Button, Paper, Typography, CircularProgress, Chip, IconButton } from '@mui/material';
import { TypingIndicator } from './TypingIndicator';
import { Zap, Rocket, MessageSquare, Send, Paperclip } from 'lucide-react';
import { getSupabaseClient } from '@lingaai/supabase-client';
import { Alert } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import type { ChatAction, ChatMessage } from '@lingaai/types';
import { ConversationHistoryDrawer } from './ConversationHistoryDrawer';
import { ICON_SIZES, ICON_STROKE } from '../../constants/icons';
import { useChatStream } from '../../hooks/useChatStream';

type Message = ChatMessage;

interface ChatClientProps {
  tenantId: string;
  userId: string;
  userName: string;
  onActionTriggered: (action: ChatAction | null) => void;
}

export interface ChatClientRef {
  notifyAgent: (message: string, fileMetadata?: any) => void;
  getUploadedFile: () => any | null;
  startNewConversation: () => void;
  loadConversation: (conversationId: string) => void;
}

export const ChatClient = forwardRef<ChatClientRef, ChatClientProps>(({ tenantId, userId, userName, onActionTriggered }, ref) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [conversationId, setConversationId] = useState<string | null>(null); // Track current conversation
  const [drawerOpen, setDrawerOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFileMetadata, setUploadedFileMetadata] = useState<any>(null);
  const [streamingMessage, setStreamingMessage] = useState<string>('');
  const [isStreamingActive, setIsStreamingActive] = useState(false);
  const streamingMessageRef = useRef<string>('');
  const supabase = getSupabaseClient();

  // Streaming chat hook
  const chatStream = useChatStream({
    onToken: (token: string) => {
      // Accumulate streaming tokens
      setStreamingMessage((prev) => {
        const newMessage = prev + token;
        streamingMessageRef.current = newMessage; // Keep ref in sync
        return newMessage;
      });
    },

    onActions: (actions: any[]) => {
      // Actions received after text completes
      const action = actions && actions.length > 0 ? actions[0] : undefined;

      // Use ref to get the latest streaming message (avoids stale closure)
      const finalMessage = streamingMessageRef.current;

      // Add final agent message with accumulated text
      setMessages((prev) => [
        ...prev,
        {
          role: 'agent',
          content: finalMessage,
          timestamp: new Date(),
          action,
        },
      ]);

      // Reset streaming state
      setStreamingMessage('');
      streamingMessageRef.current = '';
      setIsStreamingActive(false);

      // Notify parent of action
      if (action) {
        onActionTriggered(action);
      }
    },

    onComplete: (conversationId: string) => {
      // Store conversation_id from response
      setConversationId(conversationId);
    },

    onError: (error: string) => {
      console.error('Chat stream error:', error);
      setUploadError(`Chat error: ${error}`);
      setStreamingMessage('');
      setIsStreamingActive(false);
    },
  });

  const handleSend = () => {
    if (!input.trim()) return;

    // Add user message
    setMessages((prev) => [
      ...prev,
      {
        role: 'user',
        content: input,
        timestamp: new Date(),
      },
    ]);

    // Start streaming
    setIsStreamingActive(true);
    setStreamingMessage('');

    chatStream.sendMessage({
      message: input,
      tenant_id: tenantId,
      user_id: userId,
      user_name: userName,
      conversation_id: conversationId || undefined,
    });

    setInput('');
  };

  // File upload handler
  const handleFileUpload = async (file: File) => {
    const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];

    if (!validTypes.includes(file.type)) {
      setUploadError('Please upload a PDF or Excel file');
      return;
    }

    if (file.size > 52428800) {
      setUploadError('File size must be under 50MB');
      return;
    }

    setUploadingFile(true);
    setUploadError(null);

    try {
      const timestamp = Date.now();
      const tempPath = `${tenantId}/chat-uploads/${timestamp}-${file.name}`;

      const { error: uploadError } = await supabase.storage.from('portfolio-imports').upload(tempPath, file);

      if (uploadError) throw uploadError;

      setMessages((prev) => [
        ...prev,
        {
          role: 'user',
          content: `📎 Uploaded: ${file.name}`,
          timestamp: new Date(),
        },
      ]);

      const metadata = {
        name: file.name,
        size: file.size,
        type: file.type,
        temp_storage_path: tempPath,
        bucket: 'portfolio-imports',
      };

      console.log('Storing file metadata in client state:', metadata);
      setUploadedFileMetadata(metadata);

      console.log('Sending file metadata to API:', metadata);

      setIsStreamingActive(true);
      setStreamingMessage('');

      chatStream.sendMessage({
        message: `I want to upload a document: ${file.name}`,
        tenant_id: tenantId,
        user_id: userId,
        user_name: userName,
        conversation_id: conversationId || undefined,
        fileMetadata: metadata,
      });
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error instanceof Error ? error.message : 'Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  const notifyAgent = (message: string, fileMetadata?: any) => {
    // Add system message to UI immediately
    setMessages((prev) => [
      ...prev,
      {
        role: 'system',
        content: message,
        timestamp: new Date(),
      },
    ]);

    setIsStreamingActive(true);
    setStreamingMessage('');

    chatStream.sendMessage({
      message,
      tenant_id: tenantId,
      user_id: userId,
      user_name: userName,
      conversation_id: conversationId || undefined,
      fileMetadata,
    });
  };

  const startNewConversation = () => {
    setConversationId(null);
    setMessages([]);
  };

  const loadConversation = async (convId: string) => {
    setConversationId(convId);
    setMessages([]);

    // Load conversation history from API
    const apiUrl = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3000';
    try {
      const response = await fetch(`${apiUrl}/chat/conversations/${convId}/history?user_id=${userId}`, { method: 'GET' });
      if (response.ok) {
        const history = await response.json();
        // Convert AgentInputItem[] to UI Message[]
        const uiMessages: Message[] = history
          .filter((item: any) => item.type === 'message')
          .map((item: any) => ({
            role: item.role === 'user' ? 'user' : 'agent',
            content: extractMessageContent(item),
            timestamp: new Date(),
          }));
        setMessages(uiMessages);
      }
    } catch (error) {
      console.error('Failed to load conversation history:', error);
    }
  };

  // Helper to extract text content from AgentInputItem
  const extractMessageContent = (item: any): string => {
    if (item.role === 'user') {
      // User messages have content as string with format:
      // "User ID: xxx\nUser: xxx\nDate: xxx\nMessage: actual message"
      if (typeof item.content === 'string') {
        // Extract just the message part after "Message: "
        const match = item.content.match(/Message: (.+)$/s);
        return match ? match[1] : item.content;
      }
      if (Array.isArray(item.content)) {
        const textPart = item.content.find((c: any) => c.type === 'input_text');
        if (textPart?.text) {
          const match = textPart.text.match(/Message: (.+)$/s);
          return match ? match[1] : textPart.text;
        }
        return textPart?.text || '';
      }
    } else if (item.role === 'assistant') {
      // Assistant messages have content array with output_text
      if (Array.isArray(item.content)) {
        const textPart = item.content.find((c: any) => c.type === 'output_text');
        if (textPart?.text) {
          // Try to parse JSON response
          try {
            const parsed = JSON.parse(textPart.text.replace(/^```(?:json)?\s*\n?/m, '').replace(/\n?```\s*$/m, ''));
            return parsed.response || textPart.text;
          } catch {
            return textPart.text;
          }
        }
      }
    }
    return '';
  };

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    notifyAgent,
    startNewConversation,
    loadConversation,
    getUploadedFile: () => uploadedFileMetadata,
  }));

  // Auto-scroll to bottom when messages or streaming message changes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingMessage]);

  // Auto-resume last conversation on mount
  useEffect(() => {
    const loadLastConversation = async () => {
      const apiUrl = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3000';
      try {
        const response = await fetch(`${apiUrl}/chat/conversations?user_id=${userId}`, {
          method: 'GET',
        });
        if (response.ok) {
          const conversations = await response.json();
          if (conversations && conversations.length > 0) {
            // Resume most recent conversation with full history
            await loadConversation(conversations[0].id);
            return; // Don't send greeting if resuming
          }
        }
      } catch (error) {
        console.error('Failed to load conversations:', error);
      }
      // If no conversations exist, send initial greeting
      setIsStreamingActive(true);
      chatStream.sendMessage({
        message: 'hi',
        tenant_id: tenantId,
        user_id: userId,
        user_name: userName,
      });
    };

    loadLastConversation();
  }, []);

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        if (e.currentTarget === e.target) {
          setIsDragging(false);
        }
      }}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) handleFileUpload(file);
      }}
    >
      {/* Drag-Drop Overlay */}
      {isDragging && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(76, 175, 80, 0.1)',
            border: '2px dashed',
            borderColor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            borderRadius: 1,
          }}
        >
          <Typography variant="h6" color="primary">
            Drop document here to upload
          </Typography>
        </Box>
      )}

      {/* Header with History Button */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6">Portfolio Operations Analyst</Typography>
        <IconButton onClick={() => setDrawerOpen(true)} size="small" title="Conversation History">
          <MessageSquare size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />
        </IconButton>
      </Box>

      {/* Message List */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }} id="chat-messages-container">
        {messages.map((msg, idx) => (
          <Box key={idx} sx={{ mb: 2, display: 'flex', gap: 1 }}>
            {/* Sender indicator */}
            <Box
              sx={{
                width: 4,
                bgcolor: msg.role === 'user' ? 'primary.main' : 'success.main',
                borderRadius: 1,
                flexShrink: 0,
              }}
            />

            {/* Message content */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {msg.role === 'user' ? 'You' : msg.role === 'system' ? 'System' : 'Portfolio Operations Analyst'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </Typography>
              </Box>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: msg.role === 'user' ? 'action.hover' : msg.role === 'system' ? 'info.lighter' : 'action.selected',
                  borderRadius: 2,
                }}
              >
                <Box
                  sx={{
                    '& p': { margin: 0, marginBottom: 1, lineHeight: 1.6 },
                    '& p:last-child': { marginBottom: 0 },
                    '& ul, & ol': { marginTop: 0.5, marginBottom: 0.5, paddingLeft: 3 },
                    '& li': { marginBottom: 0.25 },
                    '& strong': { fontWeight: 600 },
                    '& code': {
                      bgcolor: 'action.hover',
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 0.5,
                      fontSize: '0.9em',
                      fontFamily: 'monospace',
                    },
                    '& pre': {
                      bgcolor: 'action.hover',
                      p: 1.5,
                      borderRadius: 1,
                      overflow: 'auto',
                      marginTop: 1,
                      marginBottom: 1,
                    },
                    '& pre code': {
                      bgcolor: 'transparent',
                      p: 0,
                    },
                  }}
                >
                  <ReactMarkdown>{msg.content}</ReactMarkdown>
                </Box>

                {/* Show options as button cards */}
                {msg.action?.type === 'show_options' && msg.action.options && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
                    {msg.action.options.map((option) => (
                      <Button
                        key={option.id}
                        variant="outlined"
                        onClick={() => {
                          // Send the user's choice back to the agent
                          const message = option.id === 'trial' ? 'I want to try it now' : 'I want to set up my fund';
                          setIsStreamingActive(true);
                          setStreamingMessage('');
                          chatStream.sendMessage({
                            message,
                            tenant_id: tenantId,
                            user_id: userId,
                            user_name: userName,
                            conversation_id: conversationId || undefined,
                          });
                        }}
                        sx={{
                          justifyContent: 'flex-start',
                          textAlign: 'left',
                          p: 2,
                          borderWidth: 2,
                          '&:hover': {
                            borderWidth: 2,
                            bgcolor: 'action.hover',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                          {option.id === 'trial' ? <Zap size={18} strokeWidth={1} /> : <Rocket size={18} strokeWidth={1} />}
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {option.label}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {option.description}
                            </Typography>
                          </Box>
                        </Box>
                      </Button>
                    ))}
                  </Box>
                )}

                {msg.action?.type === 'show_form' && <Chip label="📋 View Form" size="small" onClick={() => onActionTriggered(msg.action!)} sx={{ mt: 1 }} color="primary" variant="outlined" />}

                {msg.action?.type === 'show_status' && <Chip label="📊 View Status" size="small" onClick={() => onActionTriggered(msg.action!)} sx={{ mt: 1 }} color="primary" variant="outlined" />}
              </Paper>
            </Box>
          </Box>
        ))}

        {/* Show streaming message as it arrives */}
        {isStreamingActive && streamingMessage && (
          <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
            <Box
              sx={{
                width: 4,
                bgcolor: 'success.main',
                borderRadius: 1,
                flexShrink: 0,
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Portfolio Operations Analyst
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  streaming...
                </Typography>
              </Box>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: 'action.selected',
                  borderRadius: 2,
                }}
              >
                <Box
                  sx={{
                    '& p': { margin: 0, marginBottom: 1, lineHeight: 1.6 },
                    '& p:last-child': { marginBottom: 0 },
                  }}
                >
                  <ReactMarkdown>{streamingMessage}</ReactMarkdown>
                </Box>
              </Paper>
            </Box>
          </Box>
        )}

        {(isStreamingActive && !streamingMessage) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
            <TypingIndicator isTyping={true} />
            <Typography variant="body2" color="text.secondary">
              Linga is thinking...
            </Typography>
          </Box>
        )}
        {/* Invisible element at the bottom for auto-scroll */}
        <div ref={messagesEndRef} />
      </Box>

      {/* Upload Error */}
      {uploadError && (
        <Box sx={{ px: 2 }}>
          <Alert severity="error" onClose={() => setUploadError(null)}>
            {uploadError}
          </Alert>
        </Box>
      )}

      {/* Input */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.xlsx,.xls"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileUpload(file);
            e.target.value = '';
          }}
          style={{ display: 'none' }}
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={() => fileInputRef.current?.click()} disabled={uploadingFile || isStreamingActive} size="small">
            {uploadingFile ? <CircularProgress size={20} /> : <Paperclip size={20} strokeWidth={ICON_STROKE} />}
          </IconButton>
          <TextField
            fullWidth
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
            placeholder="Type a message..."
            disabled={uploadingFile || isStreamingActive}
            size="small"
          />
          <IconButton onClick={handleSend} disabled={!input.trim() || uploadingFile || isStreamingActive} color="primary">
            <Send size={20} strokeWidth={ICON_STROKE} />
          </IconButton>
        </Box>
      </Box>

      {/* Conversation History Drawer */}
      <ConversationHistoryDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        userId={userId}
        currentConversationId={conversationId}
        onSelectConversation={loadConversation}
        onNewConversation={startNewConversation}
      />
    </Box>
  );
});

ChatClient.displayName = 'ChatClient';
